import { API_BASE_URL, authService } from './authService';

export interface ValidationError {
  message?: string;
  msg?: string;
  path?: string;
  loc?: string | string[];
  category?: string;
  requirementId?: string;
}

export interface ValidationResult {
  valid: boolean;
  errors?: string[];
}

export interface StandardCode {
  code: string;
  description: string;
  source: string;
}

const VALIDATION_ENDPOINT = `${API_BASE_URL}/documents/validate-xml`;
const ECMR_CODE_LIST_SOURCE = 'UNECE eCMR local XSD (D24A/D25A invariant)';
const GENERIC_VALIDATION_ERROR = 'El XML no pudo validarse. La respuesta fue saneada para no exponer datos privados.';
const GENERIC_LOCAL_PARSE_ERROR = 'El XML no tiene una sintaxis valida. Revisa el documento antes de continuar.';
const BACKEND_VALIDATION_UNAVAILABLE_ERROR =
  'No se pudo completar la validacion autoritativa UNECE eCMR/CMR del backend. La validacion queda cerrada hasta reintentar.';
const SENSITIVE_VALIDATION_TEXT_PATTERN =
  /<[^>]+>|token=|bearer\s+|authorization|storage[_-]?key|storageBucket|downloadUrl|presigned|s3:\/\/|https?:\/\/|\bAKIA[0-9A-Z]{16}\b|\b[A-Fa-f0-9]{64}\b|\b(?:actor|principal|subject|user)[_-]?(?:user)?[_-]?id\b|\bclient[_-]?reference[_-]?id\b|\brequest[_-]?metadata\b|\bsource[_-]?ip\b|\buser[_-]?agent\b|\bip[_-]?address\b|\bxml[_-]?content\b|\bsha256\b|\bdigest\s*=/i;
const SAFE_REQUIREMENT_ID_PATTERN = /^(?:CMR|eCMR)-[A-Za-z0-9._-]+$/;
const VALIDATION_CATEGORY_LABELS: Record<string, string> = {
  cmr_business_validation: 'CMR',
  ecmr_protocol_validation: 'Protocolo eCMR',
};
const VALIDATION_CATEGORY_MESSAGES: Record<string, string> = {
  schema_profile:
    'El XML declara perfiles UNECE eCMR incompatibles. Corrige la declaracion de perfil antes de continuar.',
};

const standardCodeLists: Record<string, StandardCode[]> = {
  CountryCode: [
    { code: 'ES', description: 'Spain', source: ECMR_CODE_LIST_SOURCE },
    { code: 'FR', description: 'France', source: ECMR_CODE_LIST_SOURCE },
    { code: 'DE', description: 'Germany', source: ECMR_CODE_LIST_SOURCE },
    { code: 'IT', description: 'Italy', source: ECMR_CODE_LIST_SOURCE },
    { code: 'PT', description: 'Portugal', source: ECMR_CODE_LIST_SOURCE },
    { code: 'GB', description: 'United Kingdom', source: ECMR_CODE_LIST_SOURCE },
  ],
  TransportModeCode: [
    { code: '3', description: 'Road transport', source: ECMR_CODE_LIST_SOURCE },
  ],
  PackageTypeCode: [
    { code: 'PK', description: 'Package', source: ECMR_CODE_LIST_SOURCE },
    { code: 'CT', description: 'Carton', source: ECMR_CODE_LIST_SOURCE },
    { code: 'CS', description: 'Case', source: ECMR_CODE_LIST_SOURCE },
    { code: 'PL', description: 'Pail', source: ECMR_CODE_LIST_SOURCE },
    { code: 'DR', description: 'Drum', source: ECMR_CODE_LIST_SOURCE },
    { code: 'BX', description: 'Box', source: ECMR_CODE_LIST_SOURCE },
  ],
  DeliveryTermsCode: [
    { code: 'EXW', description: 'Ex Works', source: ECMR_CODE_LIST_SOURCE },
    { code: 'FCA', description: 'Free Carrier', source: ECMR_CODE_LIST_SOURCE },
    { code: 'CPT', description: 'Carriage Paid To', source: ECMR_CODE_LIST_SOURCE },
    { code: 'CIP', description: 'Carriage and Insurance Paid To', source: ECMR_CODE_LIST_SOURCE },
    { code: 'DAP', description: 'Delivered At Place', source: ECMR_CODE_LIST_SOURCE },
    { code: 'DDP', description: 'Delivered Duty Paid', source: ECMR_CODE_LIST_SOURCE },
  ],
};

const formatValidationError = (error: unknown): string => {
  if (typeof error === 'string') {
    return sanitizeValidationErrorText(error);
  }

  if (error && typeof error === 'object') {
    const validationError = error as ValidationError;
    const rawLocation = Array.isArray(validationError.loc)
      ? validationError.loc.join('.')
      : validationError.loc || validationError.path;
    const location = rawLocation && !SENSITIVE_VALIDATION_TEXT_PATTERN.test(rawLocation)
      ? rawLocation
      : undefined;
    const categoryLabel = validationError.category
      ? VALIDATION_CATEGORY_LABELS[validationError.category]
      : undefined;
    const requirementId =
      validationError.requirementId && SAFE_REQUIREMENT_ID_PATTERN.test(validationError.requirementId)
        ? validationError.requirementId
        : undefined;
    const fixedCategoryMessage = validationError.category
      ? VALIDATION_CATEGORY_MESSAGES[validationError.category]
      : undefined;
    const message = fixedCategoryMessage || sanitizeValidationErrorText(
      validationError.message || validationError.msg || (categoryLabel || requirementId ? GENERIC_VALIDATION_ERROR : JSON.stringify(error))
    );
    const safeRequirementPrefix = [categoryLabel, requirementId].filter(Boolean).join(' ');
    const prefixedMessage =
      safeRequirementPrefix && requirementId && !message.includes(requirementId)
        ? `${safeRequirementPrefix}: ${message}`
        : safeRequirementPrefix && !requirementId
          ? `${safeRequirementPrefix}: ${message}`
          : message;

    return location ? `${location}: ${prefixedMessage}` : prefixedMessage;
  }

  return sanitizeValidationErrorText(String(error));
};

const sanitizeValidationErrorText = (text: string): string =>
  SENSITIVE_VALIDATION_TEXT_PATTERN.test(text) ? GENERIC_VALIDATION_ERROR : text;

const extractValidationErrors = (data: unknown): string[] => {
  if (data && typeof data === 'object') {
    const responseData = data as {
      errors?: unknown[];
      detail?: unknown[] | string;
      error?: string;
      message?: string;
    };

    if (Array.isArray(responseData.errors)) {
      return responseData.errors.map(formatValidationError);
    }

    if (Array.isArray(responseData.detail)) {
      return responseData.detail.map(formatValidationError);
    }

    if (typeof responseData.detail === 'string') {
      return [formatValidationError(responseData.detail)];
    }

    if (responseData.error) {
      return [formatValidationError(responseData.error)];
    }

    if (responseData.message) {
      return [formatValidationError(responseData.message)];
    }
  }

  return ['El backend rechazo el XML'];
};

const responseHasValidationErrors = (data: unknown): boolean => {
  if (!data || typeof data !== 'object') {
    return false;
  }
  const responseData = data as {
    errors?: unknown[];
    detail?: unknown[] | string;
    error?: string;
    message?: string;
  };

  return (
    (Array.isArray(responseData.errors) && responseData.errors.length > 0) ||
    (Array.isArray(responseData.detail) && responseData.detail.length > 0) ||
    (typeof responseData.detail === 'string' && responseData.detail.trim().length > 0) ||
    (typeof responseData.error === 'string' && responseData.error.trim().length > 0) ||
    (typeof responseData.message === 'string' && responseData.message.trim().length > 0)
  );
};

const validateECMRBackendUnavailable = (xmlString: string): ValidationResult => {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, 'text/xml');
    const parseError = xmlDoc.querySelector('parsererror');
    if (parseError) {
      return {
        valid: false,
        errors: [GENERIC_LOCAL_PARSE_ERROR],
      };
    }

    return {
      valid: false,
      errors: [BACKEND_VALIDATION_UNAVAILABLE_ERROR],
    };
  } catch (error) {
    return {
      valid: false,
      errors: [GENERIC_LOCAL_PARSE_ERROR],
    };
  }
};

export const validateECMR = async (xmlString: string): Promise<ValidationResult> => {
  try {
    const response = await fetch(VALIDATION_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ xml: xmlString }),
    });

    const data = await response.json();

    if (response.ok && data?.valid === true && !responseHasValidationErrors(data)) {
      return {
        valid: true,
        errors: undefined,
      };
    }

    if (response.ok) {
      return {
        valid: false,
        errors: extractValidationErrors(data),
      };
    }

    if (response.status === 400 || response.status === 422) {
      return {
        valid: false,
        errors: extractValidationErrors(data),
      };
    }

    return {
      valid: false,
      errors: extractValidationErrors(data),
    };
  } catch {
    return validateECMRBackendUnavailable(xmlString);
  }
};

export const validateECMRDocumentFromAPI = async (documentId: string): Promise<ValidationResult> => {
  try {
    const response = await authService.authenticatedRequest(`/documents/${documentId}/validate-xml`, {
      method: 'POST',
    });

    const data = await response.json();

    if (response.ok && data?.valid === true && !responseHasValidationErrors(data)) {
      return {
        valid: true,
        errors: undefined,
      };
    }

    return {
      valid: false,
      errors: extractValidationErrors(data),
    };
  } catch {
    return {
      valid: false,
      errors: [BACKEND_VALIDATION_UNAVAILABLE_ERROR],
    };
  }
};

// UI shortlist aligned to packaged D24A codelists; backend XSD validation remains authoritative.
export const getStandardCodes = (codeType: string): StandardCode[] =>
  (standardCodeLists[codeType] || []).map((entry) => ({ ...entry }));
